import * as React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { message } from 'antd';

import {
  GET_CLIENT_FEATURE_BETAS_QUERY,
  SAVE_CLIENT_FEATURE_BETA_MUTATION,
  DELETE_CLIENT_FEATURE_BETA_MUTATION,
  REORDER_CLIENT_FEATURE_BETAS_MUTATION,
} from '@frontend/app/queries';
import {
  GetClientFeatureBetasQuery,
  GetClientFeatureBetasQuery_betas,
} from '@frontend/app/queries/types/GetClientFeatureBetasQuery';
import {
  SaveClientFeatureBetaMutation,
  SaveClientFeatureBetaMutationVariables,
} from '@frontend/app/queries/types/SaveClientFeatureBetaMutation';
import {
  DeleteClientFeatureBetaMutation,
  DeleteClientFeatureBetaMutationVariables,
} from '@frontend/app/queries/types/DeleteClientFeatureBetaMutation';

const { useEffect } = React;

export type IClientFeatureBeta = GetClientFeatureBetasQuery_betas;

export function useGetClientFeatureBetas() {
  const {
    loading,
    data: { betas = [] } = {},
    error,
    refetch,
  } = useQuery<GetClientFeatureBetasQuery>(GET_CLIENT_FEATURE_BETAS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  return { loading, betas, error, refetch };
}

export function useSaveClientFeatureBeta() {
  const [saveClientFeatureBeta, { loading, error }] = useMutation<
    SaveClientFeatureBetaMutation,
    SaveClientFeatureBetaMutationVariables
  >(SAVE_CLIENT_FEATURE_BETA_MUTATION, {
    onCompleted() {
      message.info('Saved beta configuration');
    },
    refetchQueries: [{ query: GET_CLIENT_FEATURE_BETAS_QUERY }],
  });

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  return { saveClientFeatureBeta, loading, error };
}

export function useDeleteClientFeatureBeta() {
  const [deleteClientFeatureBeta, { loading, error }] = useMutation<
    DeleteClientFeatureBetaMutation,
    DeleteClientFeatureBetaMutationVariables
  >(DELETE_CLIENT_FEATURE_BETA_MUTATION, {
    onCompleted() {
      message.info('Removed beta configuration');
    },
    refetchQueries: [{ query: GET_CLIENT_FEATURE_BETAS_QUERY }],
  });

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  return { deleteClientFeatureBeta, loading, error };
}

// Ordering-only mutation for drag-to-reorder: sends the new id order and the server renumbers
// sortOrder to match, without re-saving any beta's config. Inline types (no codegen dependency).
export function useReorderClientFeatureBetas() {
  const [reorderClientFeatureBetas, { loading, error }] = useMutation<
    { reordered: boolean },
    { orderedIds: number[] }
  >(REORDER_CLIENT_FEATURE_BETAS_MUTATION, {
    refetchQueries: [{ query: GET_CLIENT_FEATURE_BETAS_QUERY }],
  });

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  return { reorderClientFeatureBetas, loading, error };
}
